import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { DnDArea } from "components/design/UploadFileSection";
import { FileIcon } from "components/design/FileIcon";
import { Cross } from "components/icons/Cross";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { getTaxYearOptions } from "util/getTaxYearOptions";
import { TAX_RETURN_UPLOAD_ACCEPT } from "./constants";

export type UploadTaxReturnValues = {
  tax_year: string;
  file: File | null;
};

type Props = {
  latestTaxYear: number;
  isUploading: boolean;
  errorMessage: string;
  onChange: () => void;
};

export const UploadTaxReturnFields = ({
  latestTaxYear,
  isUploading,
  errorMessage,
  onChange,
}: Props) => {
  const { values, setFieldValue } =
    useTypedFormikContext<UploadTaxReturnValues>();

  const yearOptions = getTaxYearOptions(latestTaxYear + 1);
  const selectedYear =
    yearOptions.find((option) => option.value === values.tax_year) ?? null;

  const handleDrop = (dropped: File[]) => {
    if (dropped.length === 0) return;
    void setFieldValue("file", dropped[0]);
    onChange();
  };

  const handleRemoveFile = () => {
    void setFieldValue("file", null);
    onChange();
  };

  return (
    <>
      <p className="t-text-body-sm t-text-text-30">
        Upload your latest Form 1120 and we&apos;ll import your business losses,
        R&amp;D deductions, tax overpayments, and asset balances automatically.
      </p>

      <Combobox
        withForm
        name="tax_year"
        label="Tax year"
        required
        options={yearOptions}
        value={selectedYear}
        onChange={onChange}
        isClearable={false}
        isDisabled={isUploading}
        menuPortalTarget={document.body}
      />

      <DnDArea
        onDrop={handleDrop}
        accept={TAX_RETURN_UPLOAD_ACCEPT}
        multiple={false}
        disabled={isUploading}
        subText="PDF · Up to 100MB"
      />

      {values.file && (
        <div className="t-border t-border-solid t-rounded t-border-neutral-20 t-bg-surface-lighter-grey t-p-3 t-flex t-items-center t-gap-3 t-w-full">
          <FileIcon fileType={values.file.type} size="40" />
          <div className="t-flex t-flex-col t-gap-0.5 t-min-w-0 t-flex-1">
            <span className="t-text-blue-100 t-text-subtitle-sm t-truncate">
              {values.file.name}
            </span>
            <span className="t-text-neutral t-text-caption">
              {(values.file.size / 1024).toFixed(2)} KB
            </span>
          </div>
          {!isUploading && (
            <Button
              type="button"
              customType="ghost_icon"
              size="small"
              onClick={handleRemoveFile}
            >
              <Cross />
            </Button>
          )}
        </div>
      )}

      {isUploading && (
        <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-60">
          <Spinner size="small" />
          Reading your return…
        </div>
      )}

      {errorMessage && <Banner type="error" description={errorMessage} />}
    </>
  );
};
